import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TableSearchInput as ExternalInput } from '../../../components/_mui';
import { MAX_CAMPAIGN_ITEMS_PER_PAGE } from '../../../constants/campaigns';
import { MAX_INVENTORY_ITEMS_PER_PAGE } from '../../../constants/inventories';
import { INITIAL_PAGE } from '../../../constants/table';
import useSearchFilter from '../../../hooks/useSearchFilter/useSearchFilter';
import { useCampaignsInfiniteScroll, useItemsInfiniteScroll } from '../../../service/InventoryService';
import { fetchOneLinkSendsRequest } from '../../../store/actions/oneLink';
import {
  selectIsFinalOneLink,
  selectIsOneLinksFetching,
  selectOneLinks,
  selectOneLinkTotalCount,
} from '../../../store/selectors/oneLink';
import { selectOneLinksActiveView } from '../../../store/selectors/shell';
import { ICampaign } from '../../../types/campaigns';
import { IInventoryItem } from '../../../types/inventories';
import { IFetchOneLinkSendsRequestPayload, OneLinkStatusEnum } from '../../../types/oneLink';
import { FilterTypesEnum } from '../../../types/shell';
import { IFilterItem, IFiltersRef } from '../../../types/tables';
import Filters from './Filters';

interface IProps {
  currentPageSize: number;
}

const useFilters = ({ currentPageSize }: IProps) => {
  const dispatch = useDispatch();

  const filtersRef = useRef<IFiltersRef>(null);

  const isOneLinksFetching = useSelector(selectIsOneLinksFetching);
  const isFinalOneLink = useSelector(selectIsFinalOneLink);
  const oneLinkTotalCount = useSelector(selectOneLinkTotalCount);
  const activeView = useSelector(selectOneLinksActiveView);
  const oneLinkSends = useSelector(selectOneLinks);

  const [status, setStatus] = useState<OneLinkStatusEnum | ''>(OneLinkStatusEnum.Active);
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(undefined);
  const [selectedItemName, setSelectedItemName] = useState<string | undefined>(undefined);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | undefined>(undefined);
  const [selectedCampaignName, setSelectedCampaignName] = useState<string | undefined>(undefined);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);

  const handleStatusChange = useCallback((value: OneLinkStatusEnum | '') => {
    setStatus(value);
  }, []);

  const handleSetSelectedItemId = useCallback((id: string) => {
    setSelectedItemId(id);
    if (id) {
      // Clear campaign filter when inventory is selected
      setSelectedCampaignId(undefined);
      setSelectedCampaignName(undefined);
    }
  }, []);

  const handleSetSelectedCampaignId = useCallback((id: string) => {
    setSelectedCampaignId(id);
    if (id) {
      // Clear inventory filter when campaign is selected
      setSelectedItemId(undefined);
      setSelectedItemName(undefined);
    }
  }, []);

  const handleSelectDepartments = useCallback((departments: string[]) => {
    setSelectedDepartments(departments);
  }, []);

  const { SearchInput, query, resetSearch } = useSearchFilter({
    items: [],
    fieldNames: [],
    ExternalInput,
    props: { dataAction: 'one_link_search' },
  });

  const filters = useMemo(
    () => ({
      query,
      status,
      selectedDepartments,
      selectedItemId,
      selectedCampaignId,
    }),
    [query, selectedDepartments, status, selectedItemId, selectedCampaignId],
  );

  const fetchOneLinkSends = useCallback(
    (payload: Omit<IFetchOneLinkSendsRequestPayload, 'reject' | 'resolve'>) =>
      new Promise((resolve, reject) => {
        const preparedQuery = query?.toLowerCase().trim();

        // Mutual exclusivity: only send campaign_id OR item_id, not both
        // campaign_id takes precedence if both exist
        const inventoryFilter = selectedCampaignId
          ? { campaign_id: selectedCampaignId }
          : selectedItemId
          ? { item_id: selectedItemId }
          : {};

        dispatch(
          fetchOneLinkSendsRequest({
            ...(selectedDepartments && selectedDepartments.length
              ? {
                  department_ids: Array.isArray(selectedDepartments) ? selectedDepartments : [selectedDepartments],
                }
              : null),
            ...inventoryFilter,
            ...(preparedQuery && { search_query: preparedQuery }),
            ...payload,
            status,
            resolve,
            reject,
          }),
        );
      }),
    [query, dispatch, selectedDepartments, selectedItemId, selectedCampaignId, status],
  );

  const filterConfig = useMemo(() => {
    return [
      {
        label: 'Status',
        tabId: FilterTypesEnum.Status,
        value: status,
        onChange: handleStatusChange,
        options: [
          { label: 'Active', value: OneLinkStatusEnum.Active },
          { label: 'Inactive', value: OneLinkStatusEnum.NotActive },
        ],
      },
      {
        label: 'Campaign',
        tabId: FilterTypesEnum.Campaign,
        value: selectedCampaignId,
        onChange: handleSetSelectedCampaignId,
        useInfiniteScroll: true,
        isIconVisible: false,
        autocompleteConfig: {
          getId: ({ box_id }: ICampaign) => box_id,
          getName: ({ name }: ICampaign) => name,
          getImageUrl: ({ icon_url }: ICampaign) => icon_url,
          getSecondaryText: undefined,
          maxItems: MAX_CAMPAIGN_ITEMS_PER_PAGE,
        },
        dataHook: useCampaignsInfiniteScroll,
        options: [],
      },
      {
        label: 'Inventory',
        tabId: FilterTypesEnum.Item,
        value: selectedItemId,
        onChange: handleSetSelectedItemId,
        useInfiniteScroll: true,
        isIconVisible: true,
        autocompleteConfig: {
          getId: ({ item_id }: IInventoryItem) => item_id,
          getName: ({ name }: IInventoryItem) => name,
          getImageUrl: ({ image_url }: IInventoryItem) => image_url,
          getSecondaryText: ({ product_sku }: IInventoryItem) => product_sku,
          maxItems: MAX_INVENTORY_ITEMS_PER_PAGE,
        },
        dataHook: useItemsInfiniteScroll,
        options: [],
      },
    ] as IFilterItem[];
  }, [
    selectedItemId,
    selectedCampaignId,
    handleSetSelectedItemId,
    handleSetSelectedCampaignId,
    handleStatusChange,
    status,
  ]);

  const handleClearAllFilters = useCallback(() => {
    setStatus('');
    setSelectedDepartments([]);
    setSelectedItemId(undefined);
    setSelectedItemName(undefined);
    setSelectedCampaignId(undefined);
    setSelectedCampaignName(undefined);
    resetSearch();
  }, [resetSearch]);

  useEffect(() => {
    fetchOneLinkSends({
      page: INITIAL_PAGE,
      page_size: currentPageSize,
    });
    // this array has to stay empty
  }, []);

  const Component = React.useMemo(
    () => (
      <Filters
        ref={filtersRef}
        renderSearchField={SearchInput}
        query={query}
        onSearchReset={resetSearch}
        selectedDepartments={selectedDepartments}
        handleSelectDepartments={handleSelectDepartments}
        filterConfig={filterConfig}
        selectedItemName={selectedItemName}
        setSelectedItemName={setSelectedItemName}
        selectedCampaignName={selectedCampaignName}
        setSelectedCampaignName={setSelectedCampaignName}
      />
    ),
    [
      SearchInput,
      query,
      resetSearch,
      selectedDepartments,
      handleSelectDepartments,
      filterConfig,
      selectedItemName,
      selectedCampaignName,
    ],
  );

  return {
    activeView,
    fetchOneLinkSends,
    filteredOneLinks: oneLinkSends || [],
    Filters: Component,
    filtersRef,
    isFinalOneLink,
    isOneLinksFetching,
    onClear: handleClearAllFilters,
    oneLinkTotalCount,
    filters,
    query,
  };
};

export default useFilters;
